import type { Metadata } from '@philips-internal/oswald-ui-components/src/components/dynamic-fields/dynamic-fields-types.js';
import { Data } from '../../interfaces/data';
import { ScheduleItem, SuggestionGroups } from '../../interfaces/schedules-suggestions';
import { Moment as MomentType } from 'moment';
import { FilterSuggestionOrders } from '../../app/types';
import { BasicFile } from '@philips-internal/oswald-ui-components/lib/components/file-uploader/base-file-uploader-types';

export interface CurrentPatientSelected {
  id: number;
  name: string;
}

export interface HealthInsuranceCardProps {
  cardId: string;
  categoryDesc?: string;
  categoryId?: string;
  healthCardNumber?: string;
  healthInsuranceDesc: string;
  healthInsuranceId: number;
  isNewItemButton?: boolean;
  isPrivate?: boolean;
  ivaCategory?: string;
  planDesc?: string;
  planId?: string;
  sequenceVat?: number;
  walletExpiryDate?: string;
}

export interface ProcedureModel {
  examSide: ExamSide;
  externalPhysicianInfo: ExternalPhyiscianInfo;
  image: string;
  information: string;
  internalProcedureDescription: string;
  internalProcedureSequence: string;
  physicianName: string;
  physicianSequence: string;
  procedureDescription: string;
  procedureKeyWords: string;
  sequence: number;
  specialtyDescription: string;
  specialtySequence: string;
  specifiedSide: string;
  typeProcedureCode: string;
  typeProcedureDescription: string;
  viewMedicalLicenseNumberField?: string;
}

export interface ProcedureModelChange {
  examSide: ExamSide;
  externalPhysicianLicenseNumber: string;
  externalPhysicianLicenseState: Record<string, string>;
  externalPhysicianName: string;
  procedures: ProcedureModel;
}

export interface ExternalPhyiscianInfo {
  externalPhysicianLicenseNumber: string;
  externalPhysicianLicenseState: string;
  externalPhysicianName: string;
}

export interface ExamSide {
  sideInformationDomain: string;
  sideInformationSequence: number;
}

export interface AdditionalInformation {
  heightInCentimeters: number;
  saveAdditionalInfo: boolean;
  weight: number;
}

export interface AdditionalInformationValues {
  newValue: AdditionalInformation;
  oldValue: AdditionalInformation;
}

export interface EstablishmentGroupLocalizationModel {
  dsGrupoEstab: string;
  nrSequencia: number;
}

export interface QuizMetadata extends Metadata {
  answer: string;
  children: Array<QuizMetadata>;
  sequenceAnswer: string;
  visible: boolean;
}

export interface DateRange {
  end: string;
  start: string;
}

export interface PseudonymWaitingListData {
  availablePeriod: Array<string>;
  dependencies: FormDependencies;
  nrSeqAgeint: number;
  nrSeqAgeintItem: string;
  procedureList: Array<ProcedureModel>;
  urgencyReason?: UgencyReasonProps;
}

interface FormDependencies {
  calendarFinalDateInitialRange: string;
  currentPatientSelected: CurrentPatientSelected;
  healthInsuranceData: HealthInsuranceCardProps;
}

interface UgencyReasonProps {
  dsReason: string;
  nrSequence: number;
}

export interface Suggestions {
  blockedSuggestionList: Array<ScheduleItem>;
  isEnabledToScheduleWaitingList: boolean;
  isShowNotificationWaitingList: boolean;
  suggestionGroups: Array<SuggestionGroups>;
  suggestionList: Array<ScheduleItem>;
}

export interface ConfirmedSchedule {
  ieStatus: string;
  ieTipoAgendamento: string;
  nrSeqAgeintItem: number;
  nrSequencia: number;
}

export interface AppointmentConfirmationData {
  data: Array<Data>;
  isConfirmationResult: boolean;
}

export interface AppointmentCancelParameters {
  patientId: number;
  scheduleSequence: number;
  scheduleType: string;
  status: string;
}

export interface AppointmentQuizAnswerViewParameters {
  integratedScheduleItemSequence: number;
  integratedScheduleSequence: number;
  readOnly: string;
}

export interface PriorLocation {
  description: string;
  isVisible: boolean;
  sequenceNumber: number;
}

export interface VisibilityFieldsResponse {
  insuranceCardNumber: string;
  insuranceCategory: string;
  insurancePlan: string;
  locationRequired: string;
  physicianLicenseNumber: string;
  priorLocation: string;
  procedureSide: string;
}

export interface FilterSuggestionRecord {
  establishment?: EstablishmentGroupLocalizationModel;
  onlyTeleconsultation?: boolean;
  ordenation?: FilterSuggestionOrders;
}

export interface ClearTimeReservation {
  nrSeqAgeintItem: number;
}

export interface TimeReservationResponse {
  abortMessage?: string;
}

export enum FilterSuggestionType {
  AMOUNT = 'AMOUNT',
  DURATION = 'DURATION',
  TIME = 'TIME'
}

export interface DesiredAppointmentDate {
  date: MomentType;
  time: string;
}

export interface MapperGroupeSummary {
  addressNeighborhood?: string,
  addressStreet?: string,
  appointmentType?: string,
  doctorName?: string,
  dsEstablishment: string,
  image?: string,
  price?: string,
  scheduleDay?: string,
  scheduleTime?: string,
  teleconsultationType?: string
}

export interface PseudonymCustomMenssage {
  message?: string;
}

export interface CustomMessageRuleParameters{
  action?: string,
  insuranceCode?: number,
  internalProcedureNumber?: string,
  schedulingType?: string,
  specialityCode?: string
}

export interface DocumentUploadScheduleRuleResponse {
  description: string;
  required: string;
  sequence: number;
}

export interface UploadedFiles {
  file: Blob;
  info: BasicFile;
}

export interface FileUploadResponse {
  fileName: string;
  path: string;
}

export interface FileUploadRequest extends FileUploadResponse {
  sequenceNumberFileRule?: number;
}
